<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { Line } from 'vue-chartjs'
import { Account } from '../../firebase/services/accounts.service'
import { useBankStore } from '../../stores/bank'
import { Chart as ChartJS } from 'chart.js'
import { chartConfig } from '../../config/chart'

const props = defineProps<{
  accounts: Account[]
}>()

const bankStore = useBankStore()
const isLoading = ref(true)
const activeView = ref('overview')
const chartInstance = ref(null)

onMounted(async () => {
  try {
    await bankStore.fetchBanks()
  } catch (error) {
    console.error('Erro ao carregar bancos:', error)
  } finally {
    isLoading.value = false
  }
})

// Observar mudanças nas contas para atualizar o gráfico
watch(() => props.accounts, () => {
  if (chartInstance.value) {
    chartInstance.value.update()
  }
}, { deep: true })

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate()
}

// Função para agrupar contas por período
const groupAccountsByPeriod = (accounts: Account[]) => {
  const periodsMap = new Map()

  // Somar saldos iniciais dos bancos
  const initialBalance = bankStore.banks.reduce((sum, bank) => sum + (bank.initialBalance || 0), 0)

  // Ordenar contas por data
  const sortedAccounts = [...accounts].sort((a, b) => 
    new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  )

  // Encontrar o primeiro período
  let firstPeriod = sortedAccounts.length > 0 
    ? new Date(sortedAccounts[0].dueDate).toISOString().slice(0, 7)
    : new Date().toISOString().slice(0, 7)

  // Inicializar todos os períodos
  sortedAccounts.forEach(account => {
    const date = new Date(account.dueDate)
    const period = date.toISOString().slice(0, 7) // Formato: YYYY-MM

    if (!periodsMap.has(period)) {
      periodsMap.set(period, {
        receivable: 0,
        payable: 0,
        balance: 0
      })
    }
  })

  // Processar todas as contas
  let runningBalance = initialBalance
  sortedAccounts.forEach(account => {
    const date = new Date(account.dueDate)
    const period = date.toISOString().slice(0, 7)
    const value = account.value || 0
    const periodData = periodsMap.get(period)

    if (account.type === 'receivable') {
      periodData.receivable += value
      runningBalance += value
    } else {
      periodData.payable += value
      runningBalance -= value
    }
    
    periodData.balance = runningBalance
  })

  return periodsMap
}

const overviewChartData = computed(() => {
  if (!props.accounts.length) {
    return null
  }
  
  const periodsMap = groupAccountsByPeriod(props.accounts)
  const sortedPeriods = Array.from(periodsMap.keys()).sort()

  // Formatar labels para exibição (ex: "Jan/2024")
  const formattedLabels = sortedPeriods.map(period => {
    const [year, month] = period.split('-')
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${monthNames[parseInt(month) - 1]}/${year}`
  })

  return {
    labels: formattedLabels,
    datasets: [
      {
        label: 'Créditos',
        data: sortedPeriods.map(period => periodsMap.get(period).receivable || 0),
        backgroundColor: '#22c55e',
        borderRadius: 4,
        borderSkipped: false,
        order: 2
      },
      {
        label: 'Débitos',
        data: sortedPeriods.map(period => periodsMap.get(period).payable || 0),
        backgroundColor: '#ef4444',
        borderRadius: 4,
        borderSkipped: false,
        order: 3
      },
      {
        label: 'Saldo',
        data: sortedPeriods.map(period => periodsMap.get(period).balance || 0),
        type: 'line',
        borderColor: '#0891b2',
        backgroundColor: '#0891b220',
        tension: 0.4,
        borderDash: [5, 5],
        fill: true,
        order: 1
      }
    ]
  }
})

const annualChartData = computed(() => {
  if (!props.accounts.length) {
    return null
  }
  
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const currentYear = new Date().getFullYear()
  
  // Inicializar dados mensais
  const monthlyData = new Array(12).fill(0).map(() => ({ 
    receivable: 0, 
    payable: 0,
    balance: 0 
  }))

  // Somar saldos iniciais dos bancos
  const initialBalance = bankStore.banks.reduce((sum, bank) => sum + (bank.initialBalance || 0), 0)
  let runningBalance = initialBalance

  // Agrupar contas por mês
  props.accounts
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .forEach(account => {
      const date = new Date(account.dueDate)
      if (date.getFullYear() === currentYear) {
        const month = date.getMonth()
        const value = account.value || 0

        if (account.type === 'receivable') {
          monthlyData[month].receivable += value
          runningBalance += value
        } else {
          monthlyData[month].payable += value
          runningBalance -= value
        }
        
        monthlyData[month].balance = runningBalance
      }
    })

  return {
    labels: months,
    datasets: [
      {
        label: 'Créditos',
        data: monthlyData.map(d => d.receivable),
        backgroundColor: '#22c55e',
        borderRadius: 4,
        borderSkipped: false,
        order: 2
      },
      {
        label: 'Débitos',
        data: monthlyData.map(d => d.payable),
        backgroundColor: '#ef4444',
        borderRadius: 4,
        borderSkipped: false,
        order: 3
      },
      {
        label: 'Saldo Mensal',
        data: monthlyData.map(d => d.balance),
        type: 'line',
        borderColor: '#0891b2',
        backgroundColor: '#0891b220',
        tension: 0.4,
        borderDash: [5, 5],
        fill: true,
        order: 1
      }
    ]
  }
})

const monthlyChartData = computed(() => {
  if (!props.accounts.length) {
    return null
  }
  
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()
  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  
  // Inicializar dados diários
  const dailyData = new Array(daysInMonth).fill(0).map((_, index) => ({
    day: index + 1,
    receivable: 0,
    payable: 0,
    balance: 0
  }))

  // Somar saldos iniciais dos bancos
  const initialBalance = bankStore.banks.reduce((sum, bank) => sum + (bank.initialBalance || 0), 0)
  let runningBalance = initialBalance

  // Agrupar contas por dia
  props.accounts
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .forEach(account => {
      const date = new Date(account.dueDate)
      if (date.getFullYear() === currentYear && date.getMonth() === currentMonth) {
        const day = date.getDate() - 1 // Array é 0-based
        const value = account.value || 0

        if (account.type === 'receivable') {
          dailyData[day].receivable += value
          runningBalance += value
        } else {
          dailyData[day].payable += value
          runningBalance -= value
        }
        
        dailyData[day].balance = runningBalance
      }
    })

  // Preencher o saldo dos dias sem movimentação
  let lastBalance = initialBalance
  dailyData.forEach((data, index) => {
    if (index === 0) {
      data.balance = lastBalance
    } else if (data.receivable === 0 && data.payable === 0) {
      data.balance = dailyData[index - 1].balance
    }
    lastBalance = data.balance
  })

  return {
    labels: dailyData.map(d => d.day),
    datasets: [
      {
        label: 'Créditos',
        data: dailyData.map(d => d.receivable),
        backgroundColor: '#22c55e',
        borderRadius: 4,
        borderSkipped: false,
        order: 2
      },
      {
        label: 'Débitos',
        data: dailyData.map(d => d.payable),
        backgroundColor: '#ef4444',
        borderRadius: 4,
        borderSkipped: false,
        order: 3
      },
      {
        label: 'Saldo Diário',
        data: dailyData.map(d => d.balance),
        type: 'line',
        borderColor: '#0891b2',
        backgroundColor: '#0891b220',
        tension: 0.4,
        borderDash: [5, 5],
        fill: true,
        order: 1
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  aspectRatio: 2,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      padding: 10,
      callbacks: {
        label: function(context: any) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          if (context.parsed.y !== null) {
            label += new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(context.parsed.y)
          }
          return label
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: false,
      grid: {
        color: '#f1f5f9'
      },
      ticks: {
        callback: function(value: any) {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact'
          }).format(value)
        },
        font: {
          size: 11
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        font: {
          size: 11
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index' as const
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-2">
      <button
        v-for="view in ['overview', 'annual', 'monthly']"
        :key="view"
        @click="activeView = view"
        :class="[
          'px-4 py-2 rounded-lg text-sm font-medium',
          activeView === view
            ? 'bg-primary-dark text-white dark:bg-primary-light dark:text-gray-900'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
        ]"
      >
        {{ view === 'overview' ? 'Visão Geral' : view === 'annual' ? 'Anual' : 'Mensal' }}
      </button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark dark:border-primary-light"></div>
    </div>
    
    <div class="h-[400px] bg-white dark:bg-gray-800 rounded-lg p-4">
      <Line
        v-if="activeView === 'overview' && overviewChartData"
        :data="overviewChartData"
        :options="chartOptions"
        class="h-full w-full"
        @chart:render="chart => chartInstance = chart"
      />
      <Line
        v-else-if="activeView === 'annual' && annualChartData"
        :data="annualChartData"
        :options="chartOptions"
        class="h-full w-full"
        @chart:render="chart => chartInstance = chart"
      />
      <Line
        v-else-if="activeView === 'monthly' && monthlyChartData"
        :data="monthlyChartData"
        :options="chartOptions"
        class="h-full w-full"
        @chart:render="chart => chartInstance = chart"
      />
      <div v-else class="flex items-center justify-center h-full text-gray-500">
        Nenhum dado disponível para exibição
      </div>
    </div>
  </div>
</template>